/** Mega-menu "View all" layer: swaps grid ↔ banner overlay; resets on submenu leave. */

import { setSubmenuClipHeight } from '@theme/utilities';

const GRID_SELECTOR = '.mega-menu__grid';
const VIEW_ALL_SELECTOR = '[data-mega-view-all]';
const BACK_SELECTOR = '[data-mega-banner-back]';
const MENU_ITEM_SELECTOR = '.menu-list__list-item';
const SUBMENU_INNER_SELECTOR = '.menu-list__submenu-inner';
const HEADER_COMPONENT_SELECTOR = 'header-component';

function isMenuItemExpanded(grid) {
  const menuLink = grid.closest(MENU_ITEM_SELECTOR)?.querySelector('[ref="menuitem"]');
  return menuLink?.getAttribute('aria-expanded') === 'true';
}

/** Sync clip height with submenu content; skip if submenu is already closing (header-menu owns --submenu-height: 0). */
function recomputeSubmenuHeight(grid) {
  const submenuInner = grid.closest(SUBMENU_INNER_SELECTOR);
  const header = grid.closest(HEADER_COMPONENT_SELECTOR);
  if (!(submenuInner instanceof HTMLElement) || !(header instanceof HTMLElement)) return;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (!isMenuItemExpanded(grid)) return;
      const height = submenuInner.offsetHeight;
      if (height <= 0) return;
      setSubmenuClipHeight(header, height);
    });
  });
}

/** Pass focusBack for keyboard clicks only — mouse clicks must not steal focus or pointerleave loses the submenu. */
function openBanner(grid, bannerId, focusBack = false) {
  grid.setAttribute('data-active-banner', bannerId);

  grid.querySelectorAll(VIEW_ALL_SELECTOR).forEach((trigger) => {
    const isActive = trigger.getAttribute('data-mega-view-all') === bannerId;
    trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
  });

  grid.querySelectorAll('.mega-menu__banner').forEach((banner) => {
    if (banner.getAttribute('data-banner-id') === bannerId) banner.removeAttribute('hidden');
    else banner.setAttribute('hidden', '');
  });

  if (focusBack) {
    const banner = grid.querySelector(`.mega-menu__banner[data-banner-id="${CSS.escape(bannerId)}"]`);
    const backBtn = banner?.querySelector(BACK_SELECTOR);
    if (backBtn instanceof HTMLElement) backBtn.focus({ preventScroll: true });
  }

  recomputeSubmenuHeight(grid);
}

function closeBanner(grid) {
  if (!grid.hasAttribute('data-active-banner')) return;

  const bannerId = grid.getAttribute('data-active-banner');
  if (bannerId) {
    const activeBanner = grid.querySelector(`.mega-menu__banner[data-banner-id="${CSS.escape(bannerId)}"]`);
    if (activeBanner instanceof HTMLElement && activeBanner.contains(document.activeElement)) {
      const trigger = /** @type {HTMLElement | null} */ (
        grid.querySelector(`${VIEW_ALL_SELECTOR}[data-mega-view-all="${CSS.escape(bannerId)}"]`)
      );
      /* Move focus before hide so blur relatedTarget stays in-menu; otherwise header treats it as leave. */
      trigger?.focus({ preventScroll: true });
    }
  }

  grid.removeAttribute('data-active-banner');
  grid.querySelectorAll(VIEW_ALL_SELECTOR).forEach((t) => t.setAttribute('aria-expanded', 'false'));
  grid.querySelectorAll('.mega-menu__banner').forEach((b) => b.setAttribute('hidden', ''));
  recomputeSubmenuHeight(grid);
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const viewAll = target.closest(VIEW_ALL_SELECTOR);
  if (viewAll instanceof HTMLElement) {
    const grid = viewAll.closest(GRID_SELECTOR);
    const bannerId = viewAll.getAttribute('data-mega-view-all');
    if (grid && bannerId) {
      event.preventDefault();
      openBanner(grid, bannerId, event.detail === 0); // 0 ⇒ synthetic click from keyboard
    }
    return;
  }

  const back = target.closest(BACK_SELECTOR);
  if (back instanceof HTMLElement) {
    const grid = back.closest(GRID_SELECTOR);
    if (grid) {
      event.preventDefault();
      closeBanner(grid);
    }
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const target = event.target;
  if (!(target instanceof Element)) return;
  const grid = target.closest(GRID_SELECTOR);
  if (grid?.hasAttribute('data-active-banner')) {
    event.preventDefault();
    closeBanner(grid);
  }
});

/* mousedown preventDefault avoids focus-from-click + blur churn that header interprets as leaving the menu. */
document.addEventListener('mousedown', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (target.closest(VIEW_ALL_SELECTOR) || target.closest(BACK_SELECTOR)) event.preventDefault();
});

function handleMenuItemLeave(event) {
  const menuItem = event.currentTarget;
  if (!(menuItem instanceof Element)) return;
  queueMicrotask(() => {
    if (menuItem.matches(':hover') || menuItem.contains(document.activeElement)) return;
    menuItem.querySelectorAll(GRID_SELECTOR).forEach((grid) => closeBanner(grid));
  });
}

function bindResetListeners(root = document) {
  root.querySelectorAll(MENU_ITEM_SELECTOR).forEach((item) => {
    if (item.hasAttribute('data-mega-banner-bound')) return;
    item.setAttribute('data-mega-banner-bound', '');
    item.addEventListener('pointerleave', handleMenuItemLeave);
    item.addEventListener('focusout', handleMenuItemLeave);
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => bindResetListeners());
else bindResetListeners();

new MutationObserver(() => bindResetListeners()).observe(document.body, { childList: true, subtree: true });
